import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const EMPTY = { date: "", start_time: "", end_time: "", category: "Task", title: "", priority: "Medium", notes: "" };

export default function ScheduleEventFormDialog({ open, onOpenChange, defaultDate, editingEvent, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm(editingEvent ? { ...EMPTY, ...editingEvent } : { ...EMPTY, date: defaultDate });
  }, [open, editingEvent, defaultDate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async () => {
    if (!form.date || !form.start_time || !form.title) {
      toast.error("Date, start time, and title are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, priority: form.category === "Lecture" ? null : form.priority };
      if (editingEvent) {
        await api.put(`/schedule/${editingEvent.id}`, payload);
        toast.success("Schedule item updated");
      } else {
        await api.post("/schedule", payload);
        toast.success("Schedule item added");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="schedule-event-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{editingEvent ? "Edit Schedule Item" : "Add Schedule Item"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="schedule-form-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Lecture">Lecture / Class</SelectItem>
                  <SelectItem value="Task">Task</SelectItem>
                  <SelectItem value="Meeting">Meeting</SelectItem>
                  <SelectItem value="Deadline">Deadline</SelectItem>
                  <SelectItem value="Personal">Personal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input data-testid="schedule-form-date-input" type="date" value={form.date} onChange={set("date")} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{form.category === "Lecture" ? "Subject / Class Name" : "Title"}</Label>
            <Input data-testid="schedule-form-title-input" value={form.title} onChange={set("title")} placeholder={form.category === "Lecture" ? "e.g. Data Structures" : "e.g. Draft client email"} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Start Time</Label>
              <Input data-testid="schedule-form-start-input" type="time" value={form.start_time} onChange={set("start_time")} />
            </div>
            <div className="space-y-1.5">
              <Label>End Time</Label>
              <Input data-testid="schedule-form-end-input" type="time" value={form.end_time} onChange={set("end_time")} />
            </div>
          </div>
          {form.category !== "Lecture" && (
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="schedule-form-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea data-testid="schedule-form-notes-input" value={form.notes} onChange={set("notes")} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="schedule-form-cancel-button">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="schedule-form-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            {submitting ? "Saving..." : editingEvent ? "Save Changes" : "Add Item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
